import { cn } from "@gettactic/helpers";
import { useCloseAnimationTimeout } from "@gettactic/helpers/src/hooks/useCloseAnimationTimeout";
import React from "react";
import { Box } from "../../primitives";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "../../primitives/Sheet";
import { DrawerProps } from "./Drawer.types";

const DRAWER_ANIMATION_DURATION = 300;

/**
 * The app's side panel on top of `Sheet`: a title, an optional description, scrolling
 * content and a `footer` bar.
 */
export const Drawer: React.FC<DrawerProps> = ({
  children,
  title,
  description,
  onOpenChange,
  className,
  contentClassName,
  isOpen,
  footer,
  size = "3xl",
  unmountContent = false
}) => {
  const { isVisible } = useCloseAnimationTimeout(isOpen, {
    animationDuration: DRAWER_ANIMATION_DURATION
  });

  const shouldRenderContent = unmountContent ? isVisible : true;

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        size={size}
        className={cn("flex h-full flex-grow flex-col", className)}
      >
        <SheetHeader>{title && <SheetTitle>{title}</SheetTitle>}</SheetHeader>
        {description && <SheetDescription>{description}</SheetDescription>}
        <Box
          className={cn(
            "relative h-full w-full flex-grow overflow-auto p-6",
            contentClassName
          )}
        >
          {shouldRenderContent && children}
        </Box>
        {!!footer && (
          <SheetFooter
            className={"items-center justify-between border-t px-6 py-4"}
          >
            {footer}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
};
